import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';
import {saveAs} from 'file-saver/FileSaver';

import {WorkMonthModel} from './time-tracker-model/work-month.model';
import {ProjectModel} from '../project.model';
import {TimeTrackerService} from '@core/ecv-http-services/services/time-tracker.service';
import {ReportService} from '@core/ecv-http-services/services/report.service';
import {DialogService} from '../../dialog/dialog.service';
import {AlertService} from '@widgets/alert/alert.service';
import {CreateWorkTimeRangeModel} from './time-tracker-model/create-work-time-range.model';
import {EditWorkTimeRangeModel} from './time-tracker-model/edit-work-time-range.model';
import {DeleteWorkTimeRangeModel} from './time-tracker-model/delete-work-time-range.model';
import {WorkMonthApproveModel} from './time-tracker-model/work-month-approve.model';
import {ExpensesClaimModel} from './time-tracker-model/expenses-claim.model';
import {HolidayService} from '@core/ecv-http-services/services/holiday.service';
import {Ng4LoadingSpinnerService} from 'ng4-loading-spinner';

@Component({
  selector: 'app-time-tracker',
  templateUrl: './time-tracker.component.html',
  styleUrls: ['./time-tracker.component.css']
})

export class TimeTrackerComponent implements OnInit {
  year: number;
  month: number;

  workMonth: WorkMonthModel;
  currentProject: ProjectModel;

  workedHours: number;
  workedMinutes: number;
  holidays: number[] = [];
  day20DaysAhead: Date;
  foundWorkMonth: boolean;
  isWorkMonthLoading = false;

  constructor(private timeTrackerService: TimeTrackerService,
              private holidayService: HolidayService,
              private reportService: ReportService,
              private dialogService: DialogService,
              private translateService: TranslateService,
              private alertService: AlertService,
              private route: ActivatedRoute,
              private router: Router,
              private spinnerService: Ng4LoadingSpinnerService) {
    this.workMonth = new WorkMonthModel();
    this.workedHours = 0;
    this.workedMinutes = 0;
    this.foundWorkMonth = true;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.getRouteParams(params);
      return;
    });
    this.calculateDay20DaysAhead();
  }

  addWorkTimeRange(model: CreateWorkTimeRangeModel) {
    this.timeTrackerService.createWorkTimeRange(model).subscribe(() => {
      this.refreshComponent();
      this.showWorkTimeAddAlert();
    });
  }

  updateWorkTimeRange(model: EditWorkTimeRangeModel) {
    this.timeTrackerService.editWorkTimeRange(model).subscribe(() => {
      this.refreshComponent();
      this.showWorkTimeEditAlert();
    });
  }

  deleteWorkTimeRange(model: DeleteWorkTimeRangeModel) {
    this.timeTrackerService.deleteWorkTimeRange(model).subscribe(() => {
      this.refreshComponent();
      this.showWorkTimeDeleteAlert();
    });
  }

  approveWorkMonth(id: number) {
    const workMonthApprove = new WorkMonthApproveModel();
    workMonthApprove.id = id;
    this.timeTrackerService.approveWorkMonth(workMonthApprove).subscribe(() => {
      this.refreshComponent();
      this.showWorkMonthApproveAlert();
    });
  }

  generateExpertWorkMonthReport() {
    this.reportService.getExpertMonthlyReport(this.workMonth.id).subscribe(response => {
      const blob = new Blob([(<any>response).body], {type: 'application/pdf;charset=utf-8'});
      const firstName = localStorage.getItem('userFirstName');
      const lastName = localStorage.getItem('userLastName');
      const fileName = this.month + 1 + '_' + this.year + '_Monthly_report_Connectis_EXPERT_' + (firstName ? firstName : '')
        + '_' + (lastName ? lastName : '') + '.pdf';
      saveAs(blob, fileName);
    });
  }

  editExpenseClaims(workMonthId: number, expensesValue: number) {
    const expenseClaimModel = new ExpensesClaimModel();
    expenseClaimModel.value = expensesValue.toString().replace(',', '.');
    this.timeTrackerService.editExpenseClaims(workMonthId, expenseClaimModel).subscribe(() => {
      this.workMonth.expensesClaim = expensesValue;
      this.showExpensesSavedAlert();
    });
  }

  refreshComponent() {
    this.getTimeTrackerData(this.month, this.year);
  }

  showWorkTimeAddAlert() {
    this.translateService.get('ALERT.WORK_TIME_ADD', {}).subscribe((res: string) => {
      this.alertService.success(res);
    });
  }

  showWorkTimeEditAlert() {
    this.translateService.get('ALERT.WORK_TIME_EDIT', {}).subscribe((res: string) => {
      this.alertService.success(res);
    });
  }

  showWorkTimeDeleteAlert() {
    this.translateService.get('ALERT.WORK_TIME_DELETED', {}).subscribe((res: string) => {
      this.alertService.success(res);
    });
  }

  showWorkMonthApproveAlert() {
    this.translateService.get('ALERT.WORK_MONTH_APPROVED', {}).subscribe((res: string) => {
      this.alertService.success(res);
    });
  }

  showExpensesSavedAlert() {
    this.translateService.get('ALERT.EXPENSES_SAVED', {}).subscribe((res: string) => {
      this.alertService.success(res);
    });
  }

  loadDataPrevMonth() {
    let month = this.month;
    let year = this.year;
    if (this.canDecreaseMonth()) {
      if (month === 0) {
        year--;
        month = 11;
      } else {
        month--;
      }
      this.getTimeTrackerData(month, year);
    }
  }

  loadDataNextMonth() {
    let month = this.month;
    let year = this.year;
    if (this.canIncreaseMonth()) {
      if (month < 11) {
        month++;
      } else {
        month = 0;
        year++;
      }
      this.getTimeTrackerData(month, year);
    }
  }

  isDayActive(workDay: string) {
    const workDayDate = new Date(workDay);
    return this.currentProject.active && workDayDate >= this.currentProject.startDate && workDayDate <= this.currentProject.endDate;
  }

  isWeekend(weekendDay: string): boolean {
    const weekendDate = new Date(weekendDay).getDay();
    return (weekendDate === 0 || weekendDate === 6);
  }

  canDecreaseMonth() {
    const startProjectTime = this.currentProject.startDate;
    return startProjectTime.getFullYear() < this.year ||
      (startProjectTime.getFullYear() === this.year && startProjectTime.getMonth() < this.month);
  }

  canIncreaseMonth() {
    const endProjectTime = this.currentProject.endDate;
    return endProjectTime.getFullYear() > this.year ||
      (endProjectTime.getFullYear() === this.year && endProjectTime.getMonth() > this.month);
  }

  canConfirmMonth() {
    if (this.workMonth != null && this.currentProject != null) {
      const today = new Date();
      return !this.workMonth.expertConfirmed
        //&& ((this.month <= this.day20DaysAhead.getMonth() && this.year === today.getFullYear()) || this.year < today.getFullYear());
    }
    return false;
  }

  private getRouteParams(queryParams): void {
    if (!queryParams || (Object.keys(queryParams).length === 0)) {
      this.router.navigate(['/projects']);
      return;
    }
    this.currentProject = JSON.parse(queryParams['project']);
    if (!this.currentProject.startDate) {
      this.currentProject.startDate = new Date(0);
    } else {
      this.currentProject.startDate = new Date(this.currentProject.startDate);
    }
    if (!this.currentProject.endDate) {
      this.currentProject.endDate = new Date('3000-01-01');
    } else {
      this.currentProject.endDate = new Date(this.currentProject.endDate);
    }
    const date = this.currentProject.endDate > new Date() ? new Date() : this.currentProject.endDate;
    this.getTimeTrackerData(date.getMonth(), date.getFullYear());
  }

  private getTimeTrackerData(month: number, year: number): void {
    if (this.isWorkMonthLoading) {
      return;
    }
    this.isWorkMonthLoading = true;
    this.spinnerService.show();
    this.timeTrackerService.getTimeTrackerByMonthYear(this.currentProject.id, month, year).subscribe(
      (response) => {
        this.month = month;
        this.year = year;
        this.workMonth = response;
        this.foundWorkMonth = true;
        if (!this.workMonth.expensesClaim) {
          this.workMonth.expensesClaim = 0;
        }
        this.updateWorkedHours();
        this.findAllHolidays();
        this.spinnerService.hide();
        this.isWorkMonthLoading = false;
      },
      (err) => {
        this.month = month;
        this.year = year;
        this.workMonth = new WorkMonthModel();
        this.foundWorkMonth = false;
        this.workedHours = 0;
        this.workedMinutes = 0;
        this.spinnerService.hide();
        this.isWorkMonthLoading = false;
      });
  }

  private updateWorkedHours() {
    let workedMillisInMonth = 0;
    this.workMonth.workDays.forEach(workDay => {
      if (workDay.workedMilliseconds) {
        workedMillisInMonth += workDay.workedMilliseconds;
      }
    });
    this.workedHours = Math.floor(workedMillisInMonth / 1000 / 60 / 60);
    this.workedMinutes = Math.floor((workedMillisInMonth - this.workedHours * 60 * 60 * 1000) / 1000 / 60);
  }

  private openModal() {
    this.dialogService.openWorkMonthApproveModal(this);
  }

  private openExpensesClaimModal() {
    this.dialogService.openExpensesClaimModal(this, this.workMonth.expensesClaim);
  }

  private findAllHolidays() {
    this.holidayService.getHolidaysByYearMonth(this.year, this.month).subscribe(
      (response) => {
        this.holidays = response;
      });
  }

  private calculateDay20DaysAhead() {
    const day20DaysAhead = new Date();
    day20DaysAhead.setDate(new Date().getDate() + 20);
    this.day20DaysAhead = day20DaysAhead;
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/ecv-project/time-tracker/time-tracker.component.ts